/* eslint react/display-name: 0 */
import { useEffect, useState, useCallback, useMemo } from 'react';
import _ from 'lodash';
import moment from 'moment';
import { Set } from 'immutable';
import cx from 'classnames';
import Spinner from 'design-system/Spinner';
import { RowInfo } from 'react-table';

import { RealReviewLevel, getSanitizedReviewLevel } from '@scale/shared/defaults';
import { INamedObjectTypes } from '@scale/shared/types/lookup';
import { SubtaskListItem } from '@scale/shared/types/corp/task/taskListItem';
import { ReviewLevel } from '@scale/shared-types';
import SmartLink from 'react-scale/corp/components/SmartLink/SmartLink';
import { Section } from './Section';
// TODO: Migrate module so that you don’t import from the corp project in a global module
/* eslint-disable restrict-imports/restrict-imports */
import Table from 'corp/components/UI/Table';
import TaskPill from 'corp/components/UI/ProductPill';

const reviewLevelToLabel = [...RealReviewLevel, 11, 12].reduce(
  (obj: Record<string, string>, level: number | string) => {
    obj[level] = getSanitizedReviewLevel(level as ReviewLevel);
    return obj;
  },
  {},
);

const SelectCell = ({
  type,
  selected,
  onClick,
}: {
  type: string;
  selected: boolean;
  onClick: () => void;
}) => (
  <div onClick={onClick} className={cx('select-cell', { selected })}>
    <TaskPill type={type} showTitle={false} isWorker={true} disabled={!selected} />
    <style jsx>{`
      .select-cell {
        width: 24px;
        height: 24px;
        margin: 0 auto;
      }
    `}</style>
  </div>
);

const SubtaskList = ({
  subtasks,
  onChange,
  loading,
}: {
  subtasks: SubtaskListItem[];
  onChange: (subtask: SubtaskListItem[]) => void;
  loading: boolean;
}) => {
  const [selection, setSelection] = useState(Set());
  const toggleSelect = useCallback(
    (id: any) => {
      selection.has(id) ? setSelection(selection.delete(id)) : setSelection(selection.add(id));
    },
    [selection],
  );

  const selectAll = useCallback(() => {
    setSelection(Set(subtasks.map((subtask: SubtaskListItem) => subtask._id)));
  }, [subtasks]);

  const toggleSelectAll = useCallback(() => {
    selection.count() < subtasks.length ? selectAll() : setSelection(Set());
  }, [selectAll, selection, subtasks.length]);

  // Reset selection to all when new subtasks
  useEffect(() => {
    selectAll();
  }, [subtasks, selectAll]);

  // Update selected subtasks.
  useEffect(() => {
    onChange(subtasks.filter((subtask: SubtaskListItem) => selection.has(subtask._id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection, subtasks]);

  const columns = useMemo(() => {
    let columns = [
      {
        width: 80,
        sortable: false,
        headerClassName: '-cursor-pointer',

        Header: () => <div onClick={toggleSelectAll}>Select all</div>,

        Cell: ({ original }: RowInfo) =>
          (() => {
            return (
              <SelectCell
                type={original.type}
                selected={selection.has(original._id)}
                onClick={() => toggleSelect(original._id)}
              />
            );
          })(),
      },
      {
        Header: 'Project',
        accessor: '_id',
        className: 'text-center',
        Cell: ({ original }: RowInfo) => (
          <SmartLink type={INamedObjectTypes.Project} id={original.project} />
        ),
      },
      {
        Header: 'Subtask ID',
        accessor: '_id',
        className: 'text-center',
        Cell: ({ original }: RowInfo) => (
          <SmartLink type={INamedObjectTypes.Subtask} id={original._id} />
        ),
      },
      {
        Header: 'Task ID',
        accessor: '_id',
        className: 'text-center',
        Cell: ({ original }: RowInfo) => (
          <SmartLink type={INamedObjectTypes.Task} id={original.task} />
        ),
      },
      {
        Header: 'Updated at',
        accessor: 'updatedAt',
        className: 'text-center',
        Cell: ({ original }: RowInfo) =>
          moment(original.updatedAt).format('MMMM Do YYYY, h:mm:ss a'),
      },
      {
        Header: 'Last Touched By',
        accessor: 'lastTouchedBy',
        className: 'text-center text-uppercase',
        Cell: ({ original }: RowInfo) => (
          <SmartLink type={INamedObjectTypes.User} id={original.lastTouchedBy} />
        ),
      },
      {
        Header: 'Review Level',
        accessor: 'reviewLevel',
        className: 'text-center',
        Cell: ({ original }: RowInfo) => reviewLevelToLabel[original.reviewLevel],
      },
      { Header: 'Status', accessor: 'status', className: 'text-center text-uppercase' },
      { Header: 'Annotation Count', accessor: 'annotationCount', className: 'text-center' },
      {
        Header: 'Priority Objects Count',
        accessor: 'priorityObjectsCount',
        className: 'text-center',
      },
    ];
    const subsubtasksExist = _.some(subtasks, subtask => subtask._id);
    if (!subsubtasksExist) {
      columns = columns.filter(column => column.accessor !== 'subsubtask_id');
    }
    return columns;
  }, [subtasks, selection, toggleSelectAll, toggleSelect]);

  return (
    <div className="root">
      <Section name={`Select subtasks (${selection.count()}/${subtasks.length})`}>
        {loading && <Spinner style={{ margin: '2em 0' }} />}
        {!loading && (
          <div className="subtask-list">
            <Table
              data={subtasks}
              keyField="id"
              className="-highlight"
              defaultPageSize={10}
              noDataText="No subtasks found."
              columns={columns}
              getTrProps={(_state: any, rowInfo?: RowInfo): { className: string } => {
                return {
                  className: cx({
                    selected: rowInfo && selection.has(rowInfo.row._id),
                  }),
                };
              }}
            />
          </div>
        )}
      </Section>

      <style jsx>{`
        .root :global(.section-body) {
          padding: 0;
        }
      `}</style>
    </div>
  );
};

export default SubtaskList;
